/**
 * Redux slice (src/store/slices/jitsiSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../helpers/axiosconfig';

// Async thunk to generate JWT token
/**
 * Async Redux thunk: Generate Jwt Token. (jitsi/generateJwtToken)
 *
 * Calls the backend via the shared axios helper and returns a payload consumed by slice extraReducers.
 */
export const generateJwtToken = createAsyncThunk(
  'jitsi/generateJwtToken',
  async ({ bookingId, userName, userEmail, userId, role }, { rejectWithValue }) => {
    try {
      // Map role: student=participant, teacher/admin=moderator
      const mappedRole = role === 'student' ? 'participant' : 'moderator';
      
      const response = await axios.post('v1/jitsi/generate-jwt-token', {
        bookingId,
        userName,
        userEmail,
        userId,
        role: mappedRole
      });

      if (response.data.success) {
        const roomName = response.data.roomName || bookingId;
        const meetingUrl =
          response.data.meetingUrl ||
          `https://meet.speakable.online/${roomName}?jwt=${response.data.token}`;

        return {
          token: response.data.token,
          bookingId,
          role: mappedRole,
          roomName,
          meetingUrl,
        };
      } else {
        return rejectWithValue(response.data.message || 'Failed to generate JWT token');
      }
    } catch (error) {
      console.error('Error generating JWT token:', error);
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        'Failed to generate JWT token'
      );
    }
  }
);

/**
 * RTK slice definition: jitsiSlice.
 */
const jitsiSlice = createSlice({
  name: 'jitsi',
  initialState: {
    token: null,
    bookingId: null,
    role: null,
    roomName: null,
    meetingUrl: null,
    loading: false,
    error: null,
  },
  reducers: {
    /**
     * Reducer: clearToken.
     */
    clearToken: (state) => {
      state.token = null;
      state.bookingId = null;
      state.role = null;
      state.roomName = null;
      state.meetingUrl = null;
      state.error = null;
    },
    /**
     * Reducer: clearError.
     */
    clearError: (state) => {
      state.error = null;
    }
  },
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // extraReducers handler: generateJwtToken.pending
      .addCase(generateJwtToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: generateJwtToken.fulfilled
      .addCase(generateJwtToken.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.bookingId = action.payload.bookingId;
        state.role = action.payload.role;
        state.roomName = action.payload.roomName;
        state.meetingUrl = action.payload.meetingUrl;
        state.error = null;
      })
      // extraReducers handler: generateJwtToken.rejected
      .addCase(generateJwtToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.token = null;
        state.bookingId = null;
        state.role = null;
        state.roomName = null;
        state.meetingUrl = null;
      });
  },
});

export const { clearToken, clearError } = jitsiSlice.actions;
export default jitsiSlice.reducer;
